<template>
  <div class="area-boleto p-4">
    <h5 class="bold mb-4">
      <i class="fas fa-barcode"></i>
      Boletos de Pagamento
    </h5>

    <div class="alert alert-warning mb-4">
              <i class="fas fa-clock"></i>
              <div>
                <strong>Atenção - Prazo para Pagamento:</strong>
                <p class="mb-0 mt-2">Após a geração do boleto bancário da Taxa de Inscrição, é necessário aguardar, no mínimo, 02(duas) horas para efetuar o pagamento. Conforme exigência da FEBRABAN, esse prazo é necessário para a validação do código de barras perante o cedente.</p>
              </div>
    </div>

    <!-- Loading Inscrições -->
    <div v-if="loadingInscricoes" class="loading-container">
      <div class="spinner-border text-primary" role="status">
        <span class="sr-only">Carregando...</span>
      </div>
      <p class="text-muted mt-3">Carregando inscrições...</p>
    </div>

    <div v-else>
      <!-- Inscrições Disponíveis -->
      <div class="section-header mb-3">
        <i class="fas fa-clipboard-list"></i>
        <span>Minhas Inscrições</span>
      </div>

      <!-- Empty State - Sem Inscrições -->
      <div v-if="!inscricoesAtivas.length" class="empty-state">
        <i class="fas fa-clipboard-list"></i>
        <p class="empty-title">Nenhuma inscrição ativa encontrada</p>
        <p class="empty-subtitle">Você não possui inscrições ativas no momento.</p>
        <small class="empty-hint">Somente inscrições ativas podem gerar boletos.</small>
      </div>

      <template v-else>
        <!-- Desktop Table View -->
        <div class="table-responsive d-none d-md-block">
          <table class="table table-hover">
            <thead class="table-dark">
              <tr>
                <th><i class="fas fa-building"></i> Concurso</th>
                <th><i class="fas fa-briefcase"></i> Vaga</th>
                <th><i class="fas fa-dollar-sign"></i> Valor</th>
                <th><i class="fas fa-info-circle"></i> Status</th>
                <th class="text-center"><i class="fas fa-cogs"></i> Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="inscricao in inscricoesAtivas" :key="inscricao.id">
                <td>
                  <strong>{{ inscricao.concurso?.prefeitura || 'N/A' }}</strong>
                  <br>
                  <small class="text-muted">ID: #{{ inscricao.id }}</small>
                </td>
                <td>{{ inscricao.vaga?.cargo || 'N/A' }}</td>
                <td>
                  <span v-if="getValorInscricao(inscricao) > 0" class="badge badge-info">
                    R$ {{ formatarValor(getValorInscricao(inscricao)) }}
                  </span>
                  <span v-else class="badge badge-success">
                    <i class="fas fa-check-circle"></i>
                    Isento
                  </span>
                </td>
                <td>
                  <span :class="getStatusPagamentoClass(getStatusPagamento(inscricao))">
                    {{ getStatusPagamentoText(getStatusPagamento(inscricao)) }}
                  </span>
                </td>
                <td class="text-center">
                  <div class="action-buttons">
                    <!-- Isenção deferida - Não precisa de boleto -->
                    <div v-if="temIsencaoDeferida(inscricao)" class="d-flex gap-2 justify-content-center">
                      <span class="badge badge-success">
                        <i class="fas fa-check-circle"></i>
                        Isento
                      </span>
                    </div>
                    
                    <!-- Boleto já gerado - Mostrar botões de ação -->
                    <div v-else-if="inscricao.boleto_gerado === true" class="d-flex gap-2 justify-content-center">
                      <button 
                        class="btn btn-sm btn-success" 
                        @click="visualizarBoleto(inscricao)"
                        title="Visualizar/Imprimir Boleto"
                      >
                        <i class="fas fa-file-invoice"></i>
                        Ver Boleto
                      </button>
                    </div>
                    
                    <!-- Gerar boleto -->
                    <div v-else class="d-flex gap-2 justify-content-center">
                      <button 
                        class="btn btn-sm btn-primary btn-loading-inline" 
                        @click="gerarBoletoDireto(inscricao)"
                        :disabled="inscricao.gerando || getValorInscricao(inscricao) === 0"
                        :class="{ 'loading': inscricao.gerando }"
                        :title="getValorInscricao(inscricao) === 0 ? 'Não é possível gerar boleto para inscrição sem valor' : 'Gerar boleto de pagamento'"
                      >
                        <span v-if="inscricao.gerando" class="spinner-container-inline">
                          <span class="spinner-border spinner-border-sm" role="status"></span>
                          <span class="ml-1">Gerando...</span>
                        </span>
                        <span v-else>
                          <i class="fas fa-barcode"></i>
                          Gerar Boleto
                        </span>
                      </button>
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Mobile Card View -->
        <div class="mobile-cards d-md-none">
        <div 
          v-for="inscricao in inscricoesAtivas" 
          :key="inscricao.id" 
          class="mobile-card"
        >
          <div class="card-header-mobile">
            <i class="fas fa-building"></i>
            <strong>{{ inscricao.concurso?.prefeitura || 'N/A' }}</strong>
          </div>
          <div class="card-body-mobile">
            <div class="card-item">
              <i class="fas fa-hashtag"></i>
              <span class="label">ID:</span>
              <span class="value">#{{ inscricao.id }}</span>
            </div>
            <div class="card-item">
              <i class="fas fa-briefcase"></i>
              <span class="label">Vaga:</span>
              <span class="value">{{ inscricao.vaga?.cargo || 'N/A' }}</span>
            </div>
            <div class="card-item">
              <i class="fas fa-dollar-sign"></i>
              <span class="label">Valor:</span>
              <span class="value">
                <span v-if="getValorInscricao(inscricao) > 0" class="badge badge-info">
                  R$ {{ formatarValor(getValorInscricao(inscricao)) }}
                </span>
                <span v-else class="badge badge-success">
                  <i class="fas fa-check-circle"></i>
                  Isento
                </span>
              </span>
            </div>
            <div class="card-item">
              <i class="fas fa-info-circle"></i>
              <span class="label">Status:</span>
              <span :class="getStatusPagamentoClass(getStatusPagamento(inscricao))" class="value">
                {{ getStatusPagamentoText(getStatusPagamento(inscricao)) }}
              </span>
            </div>
          </div>
          <div class="card-footer-mobile">
            <!-- Isenção deferida -->
            <div v-if="temIsencaoDeferida(inscricao)" class="text-center">
              <span class="badge badge-success">
                <i class="fas fa-check-circle"></i>
                Isento
              </span>
            </div>
            <!-- Boleto já gerado -->
            <button 
              v-else-if="inscricao.boleto_gerado === true" 
              class="btn btn-success btn-block-mobile" 
              @click="visualizarBoleto(inscricao)">
              <i class="fas fa-file-invoice"></i>
              Ver Boleto
            </button>
            <!-- Gerar boleto -->
            <button 
              v-else
              class="btn btn-primary btn-block-mobile btn-loading-inline" 
              @click="gerarBoletoDireto(inscricao)"
              :disabled="inscricao.gerando || getValorInscricao(inscricao) === 0"
              :class="{ 'loading': inscricao.gerando }">
              <span v-if="inscricao.gerando" class="spinner-container-inline">
                <span class="spinner-border spinner-border-sm" role="status"></span>
                <span class="ml-1">Gerando...</span>
              </span>
              <span v-else>
                <i class="fas fa-barcode"></i>
                Gerar Boleto
              </span>
            </button>
          </div>
        </div>
      </div>
      </template>
    </div>

    <!-- Modal de Boleto -->
    <div v-if="showModal" class="modal-overlay" @click="fecharModal">
      <div class="modal-content large" @click.stop>
        <div class="modal-header">
          <h4 class="modal-title">
            <i class="fas fa-barcode"></i>
            Boleto de Pagamento
          </h4>
          <button @click="fecharModal" class="modal-close">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <!-- Loading Overlay no Modal -->
        <div v-if="loadingModal" class="modal-loading-overlay">
          <div class="spinner-border text-primary" role="status">
            <span class="sr-only">Carregando...</span>
          </div>
          <p class="mt-3">Carregando informações do boleto...</p>
        </div>

        <div class="modal-body">
          <!-- Informações da Inscrição -->
          <div v-if="inscricaoSelecionada" class="info-box mb-4">
            <h6 class="info-title">
              <i class="fas fa-info-circle"></i>
              Informações da Inscrição
            </h6>
            <div class="info-row">
              <strong><i class="fas fa-building"></i> Concurso:</strong>
              <span>{{ inscricaoSelecionada.concurso?.prefeitura }}</span>
            </div>
            <div class="info-row">
              <strong><i class="fas fa-briefcase"></i> Vaga:</strong>
              <span>{{ inscricaoSelecionada.vaga?.cargo }}</span>
            </div>
            <div class="info-row">
              <strong><i class="fas fa-dollar-sign"></i> Valor:</strong>
              <span>R$ {{ formatarValor(getValorInscricao(inscricaoSelecionada)) }}</span>
            </div>
          </div>

          <!-- Boleto já Pago -->
          <div v-if="getStatusPagamento(inscricaoSelecionada) === 'Pago'" class="alert alert-success">
            <i class="fas fa-check-circle"></i>
            <div>
              <strong>Pagamento Confirmado!</strong>
              <p class="mb-0 mt-2">O boleto para esta inscrição já foi pago e confirmado pelo sistema.</p>
            </div>
          </div>

          <!-- Boleto Disponível -->
          <div v-else-if="boletoAtual">
            <div class="boleto-card mb-4">
              <div class="boleto-icon">
                <i class="fas fa-file-invoice-dollar"></i>
              </div>
              <div class="boleto-info">
                <h6>Boleto Bancário</h6>
                <p class="mb-2">
                  <strong>Vencimento:</strong> {{ formatarData(boletoAtual.vencimento) }}
                </p>
                <p class="mb-0">
                  <strong>Valor:</strong> R$ {{ formatarValor(boletoAtual.valor) }}
                </p>
              </div>
              <div class="boleto-actions">
                <a
                  :href="boletoAtual.url_boleto"
                  class="btn btn-success"
                  target="_blank"
                >
                  <i class="fas fa-external-link-alt"></i>
                  Visualizar
                </a>
              </div>
            </div>

            <!-- Código de Barras -->
            <div v-if="boletoAtual.codigo_barras_digitavel" class="codigo-barras-box mb-4">
              <h6><i class="fas fa-barcode"></i> Código de Barras:</h6>
              <div class="codigo-barras">
                {{ boletoAtual.codigo_barras_digitavel }}
              </div>
            </div>

            <!-- Aviso sobre Prazo de Pagamento -->
            <div class="alert alert-warning mb-4">
              <i class="fas fa-clock"></i>
              <div>
                <strong>Atenção - Prazo para Pagamento:</strong>
                <p class="mb-0 mt-2">Após a geração do boleto bancário da Taxa de Inscrição, é necessário aguardar, no mínimo, 02(duas) horas para efetuar o pagamento. Conforme exigência da FEBRABAN, esse prazo é necessário para a validação do código de barras perante o cedente.</p>
              </div>
            </div>

            <!-- Instruções de Pagamento -->
            <div class="instrucoes-box">
              <h6><i class="fas fa-exclamation-circle"></i> Instruções Importantes:</h6>
              <ul>
                <li>O boleto pode ser pago em qualquer banco, lotérica ou através do Internet Banking</li>
                <li>O prazo de pagamento é até a data de vencimento indicada no boleto</li>
                <li>Após o pagamento, a confirmação pode levar até 3 dias úteis</li>
                <li>Guarde o comprovante de pagamento para eventuais comprovações</li>
              </ul>
            </div>
          </div>

          <!-- Erro ao carregar boleto -->
          <div v-else class="empty-state">
            <i class="fas fa-exclamation-triangle"></i>
            <p class="empty-title">Boleto não encontrado</p>
            <p class="empty-subtitle">Não foi possível carregar as informações do boleto.</p>
          </div>
        </div>

        <div class="modal-footer">
          <button class="btn btn-secondary" @click="fecharModal">
            <i class="fas fa-times"></i>
            Fechar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios"
import { mapGetters } from "vuex"

export default {
  name: 'BoletoPagamento',
  
  data() {
    return {
      inscricoes: [],
      boletoAtual: null,
      inscricaoSelecionada: null,
      loadingInscricoes: false,
      loadingModal: false,
      showModal: false,
    }
  },
  
  computed: {
    ...mapGetters({ profile: "profile/getProfile" }),

    inscricoesAtivas() {
      const statusPermitidos = ['A', 'D', 'I']
      return this.inscricoes.filter(inscricao => statusPermitidos.includes(inscricao.status))
    }
  },
  
  created() {
    console.log('BoletoPagamento: Componente criado')
    this.carregarInscricoes()
  },
  
  methods: {
    async carregarInscricoes() {
      try {
        this.loadingInscricoes = true
        console.log('Carregando inscrições do candidato...')
        
        const { data } = await axios.get('/ps/inscricao')
        this.inscricoes = data || []
        
        console.log('Inscrições carregadas:', this.inscricoes.length)
        
        // Verificar se existe boleto para cada inscrição
        await this.verificarBoletosExistentes()
      } catch (error) {
        console.error('Erro ao carregar inscrições:', error)
        this.inscricoes = []
        this.$sweet.sweetMensagem('Erro ao carregar inscrições.')
      } finally {
        this.loadingInscricoes = false
      }
    },

    async verificarBoletosExistentes() {
      try {
        console.log('Verificando boletos existentes...')
        console.log('Total de inscrições:', this.inscricoes.length)
        
        // Verificar boleto para cada inscrição em paralelo
        const promises = this.inscricoes.map(async (inscricao, index) => {
          try {
            console.log(`Consultando boleto para inscrição ${inscricao.id}...`)
            const { data } = await axios.get(`/ps/boleto/${inscricao.id}`)
            
            console.log(`Resposta da API para inscrição ${inscricao.id}:`, data)
            
            // Se retornou dados, o boleto existe
            if (data && data.boleto_id) {
              this.$set(this.inscricoes[index], 'boleto_gerado', true)
              this.$set(this.inscricoes[index], 'boleto_info', data)
              console.log(`✅ Boleto encontrado para inscrição ${inscricao.id}`)
            } else {
              this.$set(this.inscricoes[index], 'boleto_gerado', false)
              console.log(`⚠️ Resposta sem boleto_id para inscrição ${inscricao.id}`)
            }
          } catch (error) {
            // Se retornar 404, o boleto não existe
            if (error.response?.status === 404) {
              this.$set(this.inscricoes[index], 'boleto_gerado', false)
              console.log(`❌ Boleto não encontrado (404) para inscrição ${inscricao.id}`)
            } else {
              console.error(`❌ Erro ao verificar boleto da inscrição ${inscricao.id}:`, error)
              this.$set(this.inscricoes[index], 'boleto_gerado', false)
            }
          }
          
          return inscricao
        })
        
        await Promise.all(promises)
        
        // Forçar atualização reativa
        this.inscricoes = [...this.inscricoes]
        
        // console.log('✅ Verificação de boletos concluída')
        // console.log('Estado final das inscrições:', this.inscricoes.map(i => ({
        //   id: i.id,
        //   boleto_gerado: i.boleto_gerado,
        //   valor_vaga: i.vaga?.valor_inscricao,
        //   valor_concurso: i.concurso?.valor_inscricao,
        //   valor_calculado: this.getValorInscricao(i),
        //   situacao: i.situacao
        // })))
      } catch (error) {
        console.error('Erro ao verificar boletos existentes:', error)
      }
    },

    async visualizarBoleto(inscricao) {
      try {
        this.inscricaoSelecionada = inscricao
        this.showModal = true
        this.loadingModal = true
        this.boletoAtual = null
        
        console.log('Abrindo modal para visualizar boleto da inscrição:', inscricao.id)
        
        // Buscar boleto da inscrição
        await this.obterBoleto(inscricao.id)
      } catch (error) {
        console.error('Erro ao abrir modal de boleto:', error)
        this.$sweet.sweetMensagem('Erro ao carregar informações do boleto.')
      } finally {
        this.loadingModal = false
      }
    },

    async gerarBoletoDireto(inscricao) {
      if (!inscricao) {
        this.$sweet.sweetError('Inscrição inválida.')
        return
      }

      try {
        // Ativar loading no botão desta inscrição específica
        this.$set(inscricao, 'gerando', true)
        
        console.log('Gerando boleto para inscrição:', inscricao.id)

        const { data } = await axios.post(`/ps/boleto/gerar/${inscricao.id}`)

        if (data) {
          console.log('✅ Boleto gerado com sucesso:', data)
          
          // Mostrar mensagem de sucesso
          this.$sweet.sweetSuccess('Boleto gerado com sucesso!')
          
          // Atualizar o estado da inscrição
          this.$set(inscricao, 'boleto_gerado', true)
          this.$set(inscricao, 'boleto_info', data)
          
          // Recarregar lista para garantir sincronização
          await this.carregarInscricoes()
        } else {
          console.error('❌ Erro: Resposta vazia ao gerar boleto')
          this.$sweet.sweetError('Erro ao gerar o boleto.')
        }
      } catch (error) {
        console.error('❌ Erro ao gerar o boleto:', error)
        
        let errorMessage = 'Erro ao gerar o boleto.'
        
        if (error.response?.data?.message) {
          errorMessage = error.response.data.message
        } else if (error.response?.data?.error) {
          errorMessage = error.response.data.error
        }
        
        this.$sweet.sweetError(errorMessage)
      } finally {
        // Desativar loading no botão
        this.$set(inscricao, 'gerando', false)
      }
    },

    fecharModal() {
      this.showModal = false
      this.inscricaoSelecionada = null
      this.boletoAtual = null
    },

    async obterBoleto(inscricao_id) {
      try {
        console.log('Consultando boleto para inscrição:', inscricao_id)
        
        const { data } = await axios.get(`/ps/boleto/${inscricao_id}`)
        
        if (data) {
          this.boletoAtual = data
          console.log('Boleto encontrado:', data)
        } else {
          console.log('Nenhum boleto encontrado')
          this.boletoAtual = null
        }
      } catch (error) {
        console.error('Erro ao consultar o boleto:', error)
        
        if (error.response?.status === 404) {
          console.log('Boleto não existe ainda')
          this.boletoAtual = null
        } else {
          this.$sweet.sweetMensagem('Erro ao consultar o boleto.')
        }
      }
    },


    temIsencaoDeferida(inscricao) {
      if (!inscricao) return false
      
      // Verificar se tem isenção deferida
      if (inscricao.situacao === 'D' || inscricao.situacao === 'Deferida') {
        return true
      }
      
      // Verificar se tem isenção solicitada mas ainda não processada
      if (inscricao.isencao_solicitada && (inscricao.situacao === 'P' || inscricao.situacao === 'Pendente')) {
        return false // Ainda pendente, não é deferida
      }
      
      return false
    },

    getValorInscricao(inscricao) {
      if (!inscricao) return 0
      
      // Se tem isenção deferida, valor é zero
      if (this.temIsencaoDeferida(inscricao)) {
        return 0
      }
      
      // Prioridade: valor da vaga > valor do concurso > 0
      if (inscricao.vaga?.valor_inscricao) {
        const valor = parseFloat(inscricao.vaga.valor_inscricao)
        return isNaN(valor) ? 0 : valor
      }
      
      if (inscricao.concurso?.valor_inscricao) {
        const valor = parseFloat(inscricao.concurso.valor_inscricao)
        return isNaN(valor) ? 0 : valor
      }
      
      return 0
    },

    getStatusPagamento(inscricao) {
      if (!inscricao) return 'N/A'
      
      // Se a inscrição está cancelada
      if (inscricao.status === 'C') {
        return 'Cancelado'
      }
      
      // Verificar se tem isenção deferida
      if (inscricao.situacao === 'D' || inscricao.situacao === 'Deferida') {
        return 'Isento'
      }
      
      // Verificar se tem boleto gerado
      if (inscricao.boleto_gerado === true && inscricao.boleto_info) {
        const statusBoleto = inscricao.boleto_info.status_boleto || inscricao.situacao
        
        // Verificar se está vencido
        if (statusBoleto === 'Emitido' && inscricao.boleto_info.vencimento) {
          const vencimento = new Date(inscricao.boleto_info.vencimento)
          const hoje = new Date()
          if (hoje > vencimento) {
            return 'Vencido'
          }
        }
        
        return statusBoleto || 'Emitido'
      }
      
      // Se não tem boleto, verificar pela situação retornada pelo backend
      if (inscricao.situacao) {
        if (inscricao.situacao === 'Sem Boleto Emitido' || inscricao.situacao === 'Status Indefinido') {
          return 'Pendente de Emissão'
        }
        return inscricao.situacao
      }
      
      // Status padrão quando não há boleto
      return 'Pendente de Emissão'
    },

    getStatusPagamentoClass(status) {
      const classes = {
        'Pendente de Emissão': 'badge badge-warning',
        'Pendente': 'badge badge-warning',
        'Emitido': 'badge badge-info',
        'Pago': 'badge badge-success',
        'Vencido': 'badge badge-danger',
        'Cancelado': 'badge badge-secondary',
        'Isento': 'badge badge-success',
        'Isenção Solicitada': 'badge badge-warning',
        'Isenção Pendente': 'badge badge-warning',
        'Deferida': 'badge badge-success',
        'Indeferida': 'badge badge-danger'
      }
      return classes[status] || 'badge badge-secondary'
    },

    getStatusPagamentoText(status) {
      const textos = {
        'Pendente de Emissão': 'Pendente de Emissão',
        'Pendente': 'Pendente',
        'Emitido': 'Boleto Emitido',
        'Pago': 'Pago',
        'Vencido': 'Vencido',
        'Cancelado': 'Cancelado',
        'Isento': 'Isento',
        'Isenção Solicitada': 'Isenção Solicitada',
        'Isenção Pendente': 'Isenção Pendente',
        'Deferida': 'Isenção Deferida',
        'Indeferida': 'Isenção Indeferida',
        'Sem Boleto Emitido': 'Pendente de Emissão'
      }
      return textos[status] || status || 'N/A'
    },

    formatarData(data) {
      if (!data) return '-';
      
      try {
        // Se já está no formato DD/MM/YYYY
        if (typeof data === 'string' && data.includes('/')) return data;
        
        // Se está no formato DD.MM.YYYY
        if (typeof data === 'string' && data.includes('.')) {
          return data.replace(/\./g, '/');
        }
        
        // Se a data vier apenas com a data (sem hora), usar diretamente
        if (typeof data === 'string' && data.match(/^\d{4}-\d{2}-\d{2}$/)) {
          const [year, month, day] = data.split('-');
          return `${day}/${month}/${year}`;
        }
        
        // Tratar formato do banco: YYYY-MM-DD HH:mm:ss (com espaço)
        if (typeof data === 'string') {
          let dateString = data.replace('Z', '');
          
          // Se tiver formato com espaço (formato do banco: YYYY-MM-DD HH:mm:ss)
          if (dateString.match(/^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2}/)) {
            const [datePart] = dateString.split(' ');
            const [year, month, day] = datePart.split('-');
            return `${day}/${month}/${year}`;
          }
          
          // Para datas ISO com timezone, remover o 'Z' e tratar como local
          if (dateString.includes('T')) {
            dateString = dateString.replace('Z', '');
            if (dateString.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/)) {
              const [datePart] = dateString.split('T');
              const [year, month, day] = datePart.split('-');
              return `${day}/${month}/${year}`;
            }
          }
        }
        
        // Criar data e usar métodos locais
        const dateObj = new Date(data);
        if (isNaN(dateObj.getTime())) return '-';
        
        const dia = String(dateObj.getDate()).padStart(2, '0');
        const mes = String(dateObj.getMonth() + 1).padStart(2, '0');
        const ano = dateObj.getFullYear();
        return `${dia}/${mes}/${ano}`;
      } catch (error) {
        return '-';
      }
    },

    formatarValor(valor) {
      if (!valor && valor !== 0) return '0,00'
      return parseFloat(valor).toFixed(2).replace('.', ',')
    },

    formatarCPF(cpf) {
      if (!cpf) return ''
      return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
    }
  },
}
</script>

<style lang="sass" scoped>
.area-boleto
  background-color: #fff
  padding: 25px 15px
  border-radius: 8px
  box-shadow: 0 2px 4px rgba(0,0,0,0.1)
  
  h5
    font-size: 20px
    font-weight: 600
    text-align: center
    color: #2c3e50
    margin-bottom: 25px
    
    i
      margin-right: 10px
      color: #007bff

// Section headers
.section-header
  font-size: 16px
  font-weight: 600
  color: #2c3e50
  padding: 10px 15px
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)
  border-left: 4px solid #007bff
  border-radius: 4px
  margin-bottom: 20px
  
  i
    margin-right: 8px
    color: #007bff
  
  span
    vertical-align: middle

// Loading States
.loading-container
  display: flex
  flex-direction: column
  justify-content: center
  align-items: center
  padding: 80px 20px
  min-height: 200px
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)
  border-radius: 8px
  animation: fadeIn 0.3s ease

  .spinner-border
    width: 3rem
    height: 3rem
    border-width: 0.3rem

  p
    font-size: 15px
    color: #6c757d
    margin-top: 15px

// Empty state
.empty-state
  text-align: center
  padding: 60px 30px
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)
  border-radius: 12px
  border: 2px dashed #dee2e6
  margin: 20px 0
  animation: fadeIn 0.5s ease
  
  i
    font-size: 64px
    color: #cbd5e0
    margin-bottom: 20px
    display: block
    animation: pulse 2s ease-in-out infinite
  
  .empty-title
    font-size: 18px
    font-weight: 600
    color: #495057
    margin: 15px 0 10px 0
  
  .empty-subtitle
    font-size: 14px
    color: #6c757d
    margin: 0 0 20px 0

// Tables
.table-responsive
  margin-top: 15px
  border-radius: 8px
  overflow: hidden
  box-shadow: 0 2px 4px rgba(0,0,0,0.1)

.table
  margin-bottom: 0
  
  thead.table-dark
    background: linear-gradient(135deg, #2c3e50 0%, #34495e 100%)
    
    th
      color: #fff
      font-weight: 600
      border: none
      padding: 12px
      font-size: 14px
      
      i
        margin-right: 5px
        opacity: 0.8
  
  tbody
    tr
      transition: all 0.3s ease
      
      &:hover
        background-color: #f8f9fa
        transform: scale(1.002)
    
    td
      padding: 12px
      vertical-align: middle
      font-size: 14px

// Badge Status
.badge
  font-size: 12px
  padding: 5px 12px
  border-radius: 12px
  font-weight: 600
  display: inline-block

  &.badge-warning
    background: #ffc107
    color: #000 !important

  &.badge-success
    background: #28a745
    color: #fff !important

  &.badge-danger
    background: #dc3545
    color: #fff !important
  
  &.badge-info
    background: #17a2b8
    color: #fff !important

  &.badge-secondary
    background: #6c757d
    color: #fff !important

// Action buttons
.action-buttons
  display: flex
  gap: 8px
  justify-content: center
  align-items: center
  flex-wrap: wrap

// Botão com loading inline (na tabela)
.btn-loading-inline
  position: relative
  min-width: 110px
  transition: all 0.3s ease
  
  &.loading
    cursor: not-allowed
    opacity: 0.8
  
  .spinner-container-inline
    display: flex
    align-items: center
    justify-content: center
    gap: 6px
    
    .spinner-border-sm
      width: 14px
      height: 14px
      border-width: 2px
    
    .ml-1
      margin-left: 4px

.d-flex
  display: flex
  
  &.gap-2
    gap: 8px
  
  &.justify-content-center
    justify-content: center

// Modal
.modal-overlay
  position: fixed
  top: 0
  left: 0
  width: 100%
  height: 100%
  background: rgba(0, 0, 0, 0.5)
  z-index: 1050
  display: flex
  align-items: center
  justify-content: center
  padding: 20px
  animation: fadeIn 0.3s ease

.modal-content
  background: white
  border-radius: 10px
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3)
  max-width: 700px
  width: 90vw
  max-height: 90vh
  overflow: auto
  position: relative
  animation: slideInUp 0.3s ease
  
  &.large
    max-width: 900px

.modal-header
  display: flex
  justify-content: space-between
  align-items: center
  padding: 20px 25px
  border-bottom: 1px solid #e9ecef
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)
  border-radius: 10px 10px 0 0
  
  .modal-title
    margin: 0
    font-size: 18px
    font-weight: 600
    color: #2c3e50
    
    i
      margin-right: 8px
      color: #007bff

.modal-close
  background: none
  border: none
  font-size: 20px
  color: #6c757d
  cursor: pointer
  padding: 5px
  border-radius: 50%
  width: 35px
  height: 35px
  display: flex
  align-items: center
  justify-content: center
  transition: all 0.3s ease
  
  &:hover
    background: #e9ecef
    color: #dc3545

// Modal Loading Overlay
.modal-loading-overlay
  position: absolute
  top: 0
  left: 0
  right: 0
  bottom: 0
  background: rgba(255, 255, 255, 0.95)
  z-index: 10
  display: flex
  flex-direction: column
  align-items: center
  justify-content: center
  border-radius: 10px
  
  .spinner-border
    width: 3rem
    height: 3rem
    border-width: 0.3rem
  
  p
    margin-top: 15px
    color: #6c757d
    font-size: 15px

.modal-body
  padding: 25px
  max-height: calc(90vh - 180px)
  overflow-y: auto

.modal-footer
  display: flex
  justify-content: flex-end
  gap: 10px
  padding: 20px 25px
  border-top: 1px solid #e9ecef
  background: #f8f9fa
  border-radius: 0 0 10px 10px

// Info box (dentro do modal)
.info-box
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)
  padding: 20px
  border-radius: 8px
  border-left: 5px solid #2196f3
  
  .info-title
    font-size: 15px
    font-weight: 600
    color: #2c3e50
    margin-bottom: 15px
    display: flex
    align-items: center
    gap: 8px
    
    i
      color: #2196f3
  
  .info-row
    display: flex
    justify-content: space-between
    align-items: center
    padding: 8px 0
    border-bottom: 1px solid rgba(255,255,255,0.5)
    
    &:last-child
      border-bottom: none
      
      strong
        color: #2c3e50
      font-size: 14px
      
      i
        margin-right: 6px
        color: #2196f3
      
      span
        color: #495057
        font-size: 14px

// Boleto Card
.boleto-card
  background: linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%)
  padding: 20px
  border-radius: 8px
  border-left: 5px solid #28a745
  display: flex
  align-items: center
  gap: 20px
  box-shadow: 0 2px 8px rgba(0,0,0,0.1)
  animation: slideInUp 0.4s ease
  
  .boleto-icon
    font-size: 48px
    color: #28a745
    flex-shrink: 0
  
  .boleto-info
    flex: 1
    
    h6
      margin: 0 0 10px 0
      font-size: 16px
      font-weight: 600
      color: #2c3e50
    
    p
      color: #495057
      font-size: 13px
      margin: 0
      line-height: 1.6
  
  .boleto-actions
    flex-shrink: 0
    
    .btn
      white-space: nowrap

// Código de Barras
.codigo-barras-box
  background: #f8f9fa
  border: 1px solid #dee2e6
  border-radius: 8px
  padding: 15px
  
  h6
    margin: 0 0 10px 0
    font-size: 14px
    font-weight: 600
    color: #2c3e50
    
    i
      margin-right: 6px
      color: #007bff
  
  .codigo-barras
    font-family: 'Courier New', monospace
    font-size: 13px
    background: white
    padding: 10px
    border-radius: 4px
    border: 1px solid #dee2e6
    color: #2c3e50
    text-align: center
    word-break: break-all
    line-height: 1.8

// Instruções
.instrucoes-box
  background: linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%)
  border-left: 5px solid #ffc107
  border-radius: 8px
  padding: 20px
  box-shadow: 0 2px 4px rgba(0,0,0,0.08)
  
  h6
    margin: 0 0 15px 0
    color: #856404
    font-weight: 600
    font-size: 15px
    
    i
      margin-right: 8px
  
  ul
    margin: 0
    padding-left: 20px
    
    li
      color: #856404
      margin-bottom: 10px
      line-height: 1.7
      font-size: 13px
      
      &:last-child
        margin-bottom: 0

// Alerts
.alert
  padding: 20px
  border-radius: 8px
  display: flex
  align-items: flex-start
  gap: 15px
  animation: slideInDown 0.4s ease
  
  i
    font-size: 24px
    margin-top: 2px
    flex-shrink: 0
  
  &.alert-success
    background: linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%)
    border: 1px solid #c3e6cb
    border-left: 5px solid #28a745
    color: #155724
    
    i
      color: #28a745

  &.alert-warning
    background: linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%)
    border: 1px solid #ffeaa7
    border-left: 5px solid #ffc107
    color: #856404
    
    i
      color: #ffc107
  
  div
    flex: 1
    
    strong
      display: block
      font-size: 16px
      margin-bottom: 8px
      font-weight: 600
    
    p
      font-size: 14px
      line-height: 1.6
      margin: 0

// Buttons
.btn
  border-radius: 6px
  padding: 10px 20px
  font-weight: 600
  transition: all 0.3s ease
  border: none
  font-size: 14px
  
  &:hover:not(:disabled)
    transform: translateY(-2px)
    box-shadow: 0 4px 12px rgba(0,0,0,0.2)
  
  &:active:not(:disabled)
    transform: translateY(0)
  
  &:disabled
    opacity: 0.6
    cursor: not-allowed
    transform: none
  
  i
    margin-right: 8px
  
  &.btn-sm
    padding: 6px 12px
    font-size: 13px
    
    i
      margin-right: 5px
  
  &.btn-lg
    padding: 12px 30px
    font-size: 16px
    
    i
      margin-right: 10px
  
  &.btn-primary
    background: linear-gradient(135deg, #007bff 0%, #0056b3 100%)
    
    &:hover:not(:disabled)
      background: linear-gradient(135deg, #0056b3 0%, #004085 100%)
  
  &.btn-success
    background: linear-gradient(135deg, #28a745 0%, #1e7e34 100%)
    
    &:hover:not(:disabled)
      background: linear-gradient(135deg, #1e7e34 0%, #155724 100%)
  
  &.btn-secondary
    background: linear-gradient(135deg, #6c757d 0%, #5a6268 100%)
    
    &:hover:not(:disabled)
      background: linear-gradient(135deg, #5a6268 0%, #545b62 100%)

// Animations
@keyframes fadeIn
  from
    opacity: 0
  to
    opacity: 1

@keyframes slideInUp
  from
    opacity: 0
    transform: translateY(20px)
  to
    opacity: 1
    transform: translateY(0)

@keyframes slideInDown
  from
    opacity: 0
    transform: translateY(-20px)
  to
    opacity: 1
    transform: translateY(0)

@keyframes pulse
  0%, 100%
    transform: scale(1)
  50%
    transform: scale(1.05)

// Utility classes
.weight-400
  font-weight: 400

// Responsividade
@media (max-width: 767px)
  // Mostrar cards mobile e esconder tabela desktop
  .mobile-cards.d-md-none
    display: flex !important
  
  .table-responsive.d-none.d-md-block
    display: none !important

@media (min-width: 768px)
  // Mostrar tabela desktop e esconder cards mobile
  .table-responsive.d-none.d-md-block
    display: block !important
  
  .mobile-cards.d-md-none
    display: none !important

@media (max-width: 768px)
  .modal-content
    width: 95vw
  
  .table
    font-size: 12px
    
    th, td
      padding: 8px
  
  .boleto-card
    flex-direction: column
    text-align: center
    padding: 15px
    
    .boleto-icon
      font-size: 36px
    
    .boleto-actions
      width: 100%
      
      .btn
        width: 100%
  
  .area-boleto
    padding: 15px 10px
    
    h5
      font-size: 18px

@media (max-width: 576px)
  .modal-content
    width: 98vw
  
  .section-header
    font-size: 14px
    padding: 8px 12px
  
  .empty-state
    padding: 40px 15px
    
    i
      font-size: 48px

  .card-body-mobile
    padding: 8px 10px
    gap: 8px 10px
    
    .card-item
      gap: 3px
      
      i
        font-size: 12px
      
      .label
        font-size: 10px
      
      .value
        font-size: 11px
        line-height: 1.2
        
        .badge
          font-size: 10px
          padding: 3px 6px

  .card-header-mobile
    padding: 8px 10px
    
    i
      font-size: 14px
    
    strong
      font-size: 12px

  .card-footer-mobile
    padding: 8px 10px
    
    .btn-block-mobile
      padding: 8px 10px
      font-size: 12px
  
  .empty-hint
    font-size: 12px
    color: #adb5bd
    font-style: italic
    margin-top: 10px

// Mobile Cards
.mobile-cards
  display: none
  flex-direction: column
  gap: 10px
  margin-top: 15px

// Desktop Table - esconder em mobile por padrão
.table-responsive.d-none.d-md-block
  display: none !important

// Mobile Cards - mostrar apenas em mobile
.mobile-cards.d-md-none
  display: flex !important
  visibility: visible !important
  opacity: 1 !important

// Desktop Table - mostrar apenas em desktop
@media (min-width: 768px)
  .table-responsive.d-none.d-md-block
    display: block
  
  .mobile-cards.d-md-none
    display: none

.mobile-card
  background: #fff
  border-radius: 8px
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1)
  overflow: hidden
  transition: all 0.3s ease
  
  &:hover
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15)
    transform: translateY(-2px)

.card-header-mobile
  background: linear-gradient(135deg, #2c3e50 0%, #34495e 100%)
  color: #fff
  padding: 10px 12px
  display: flex
  align-items: center
  gap: 8px
  
  i
    font-size: 16px
    opacity: 0.9
  
  strong
    font-size: 13px
    font-weight: 600
    flex: 1
    line-height: 1.3

.card-body-mobile
  padding: 10px 12px
  display: grid
  grid-template-columns: repeat(2, 1fr)
  gap: 10px 12px
  
  .card-item
    display: flex
    flex-direction: column
    align-items: flex-start
    gap: 4px
    
    i
      color: #007bff
      font-size: 14px
      flex-shrink: 0
      margin-bottom: 2px
    
    .label
      font-weight: 600
      color: #6c757d
      font-size: 11px
      line-height: 1.2
    
    .value
      color: #2c3e50
      font-size: 12px
      line-height: 1.3
      text-align: left
      word-break: break-word
      width: 100%
      
      .badge
        font-size: 11px
        padding: 4px 8px
        white-space: nowrap

.card-footer-mobile
  padding: 10px 12px
  background: #f8f9fa
  border-top: 1px solid #e9ecef
  
  .btn-block-mobile
    width: 100%
    padding: 8px 12px
    font-size: 13px
    font-weight: 600
    display: flex
    align-items: center
    justify-content: center
    gap: 6px
    
    i
      font-size: 14px
</style>
